import { z } from "zod";
import { Schedule, Rule, Features, FeaturesState, Feature } from "@visioni/schedules";

/**
 * Alarm feature names - the canonical set of features controlled by alarm schedules.
 * These are the exact feature names that alarm schedules must define.
 */
export const AlarmFeatureName = z.enum(["monitoring", "recording", "sirens", "strobes"]);
export type AlarmFeatureName = z.infer<typeof AlarmFeatureName>;

/**
 * Alarm feature states - the possible states for alarm features.
 */
export const AlarmFeatureState = z.enum(["armed", "disarmed"]);
export type AlarmFeatureState = z.infer<typeof AlarmFeatureState>;

/**
 * Alarm feature - uses the base Feature schema with alarm-specific states.
 */
export const AlarmFeature = Feature(AlarmFeatureState);
export type AlarmFeature = z.infer<typeof AlarmFeature>;

/**
 * Alarm features - a record mapping alarm feature names to their definitions.
 */
export const AlarmFeatures = Features(AlarmFeatureName, AlarmFeatureState);
export type AlarmFeatures = z.infer<typeof AlarmFeatures>;

/**
 * Alarm rule features state - partial record of feature name to state for rules.
 * Rules don't need to specify all features, only the ones they affect.
 */
export const AlarmRuleFeaturesState = FeaturesState(AlarmFeatureName, AlarmFeatureState);
export type AlarmRuleFeaturesState = z.infer<typeof AlarmRuleFeaturesState>;

/**
 * Alarm rule - a Rule with typed alarm features and states.
 */
export const AlarmRule = Rule(AlarmFeatureName, AlarmFeatureState);
export type AlarmRule = z.infer<typeof AlarmRule>;

/**
 * Alarm schedule - a Schedule with typed alarm features and states.
 * This is the typed variant of Schedule for alarm-specific schedules.
 */
export const AlarmSchedule = Schedule(AlarmFeatureName, AlarmFeatureState);
export type AlarmSchedule = z.infer<typeof AlarmSchedule>;
